import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Sparkles, LayoutDashboard, Compass, FileText, Target, Mail } from "lucide-react";

export default function PrincipalDemoAccess() {
  return (
    <>
      <PageHead
        title="Principal Demo Access | WeKIT™"
        description="Login credentials and a quick tour for principals previewing WeKIT™ Career Clarity 360."
        path="/principal-demo-access"
      />
      <main className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground mb-4">
              <Sparkles className="h-3.5 w-3.5" /> For visiting principals
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Principal Demo Access
            </h1>
            <p className="text-lg text-muted-foreground">
              Sign in with the shared demo account to preview the full{" "}
              <span className="font-semibold text-foreground">WeKIT™ Career Clarity 360</span>{" "}
              experience — exactly what your school would see on day one.
            </p>
          </div>


          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Step 1 — Sign in</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Demo credentials are issued privately per school so your visit stays scoped and
                secure. Ask your WeKIT contact for a one-time principal login, or request one below.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button asChild>
                  <a href="mailto:hello@wekit.ai?subject=Principal%20demo%20access">
                    <Mail className="mr-2 h-4 w-4" /> Request demo access
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/auth">
                    Open sign-in page <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Step 2 — Start the cinematic welcome</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                After signing in, open{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/meeting</code> to launch
                Meeting Mode™ — a 5-second branded intro that segues into the institution dashboard.
                Perfect for opening a live boardroom demo.
              </p>
              <Button asChild variant="outline">
                <Link to="/meeting">
                  Go to /meeting <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Step 3 — Explore the Institution Dashboard</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Head to{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/institution</code> to see
                cohort intelligence, the Morning Briefing, and the executive brief — populated with
                sample school data.
              </p>
              <Button asChild variant="outline">
                <Link to="/institution">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Go to /institution
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="mb-6 border-primary/30">
            <CardHeader>
              <CardTitle>Step 4 — Try Career Clarity 360™ as a student</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This is what every student in your school experiences — the flagship product you're
                licensing. Walk through the assessment, see the AI-generated report, and explore the
                learner dashboard exactly as a Grade 9–12 student would.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <Button asChild>
                  <Link to="/start">
                    <Compass className="mr-2 h-4 w-4" /> Take the assessment
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/report">
                    <FileText className="mr-2 h-4 w-4" /> View sample report
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/dashboard">
                    <Target className="mr-2 h-4 w-4" /> Student dashboard
                  </Link>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Prefer a guided tour? Open the{" "}
                <Link to="/student-quickstart" className="underline underline-offset-2">
                  Student Quickstart
                </Link>{" "}
                page. Also try{" "}
                <Link to="/journey" className="underline underline-offset-2">/journey</Link>,{" "}
                <Link to="/vision-board" className="underline underline-offset-2">/vision-board</Link>, and{" "}
                <Link to="/coach" className="underline underline-offset-2">/coach</Link>.
              </p>
            </CardContent>
          </Card>



          <Card>
            <CardHeader>
              <CardTitle>Want to see your own school's branding?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                From the admin hub, open{" "}
                <Link to="/admin/demo-institution" className="underline underline-offset-2">
                  Admin → Demo Branding
                </Link>{" "}
                and switch the active profile. Logo, colors, tagline, and principal name update
                instantly across <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/meeting</code>,{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/institution</code>, and
                the Morning Briefing PDF. No new login needed.
              </p>
            </CardContent>
          </Card>

          <p className="mt-10 text-xs text-muted-foreground text-center">
            The demo account is shared and read-mostly — feel free to click around.
          </p>
        </div>
      </main>
    </>
  );
}
